import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuChevronDown, LuChevronUp } from 'react-icons/lu'
import { Box, Text } from '@chakra-ui/react'
import debounce from 'lodash.debounce'

import theme from 'styles/theme'
import { useClickOutside } from 'utils/useClickOutside'

import { ErrorMessage } from '../ErrorMessage/styles'

import StyledSearchInput from './styledInput'
import { Button, Icon, Option, OptionRadio, Options, SelectWrapper } from './styles'
import { SelectOption, useSelect } from './useSelect'

interface SelectProps {
  name: string
  options: SelectOption[]
  defaultOption?: SelectOption | null
  placeholder?: string
  error?: any
  disabled?: boolean
  onChange?: (value: SelectOption) => void
  icon?: string
  touched?: boolean
  onClear?: () => void
  onSearchTermChange: (search: string) => void
  shouldHideClearButton?: boolean
  className?: string
  zIndex?: number
}

export const SelectWithSearch: React.FC<SelectProps> = ({
  options,
  defaultOption,
  placeholder,
  error,
  disabled,
  onChange,
  icon,
  touched,
  onClear,
  shouldHideClearButton,
  className,
  onSearchTermChange,
  zIndex,
}) => {
  const { isSelectOpen, toggleSelect, chosenOption, chooseOption, clearOption, closeSelect } = useSelect(
    onChange,
    defaultOption,
  )
  const [searchTerm, setSearchTerm] = useState(defaultOption?.label ?? '')

  const wrapperRef = useRef<HTMLDivElement>(null!)
  const { t } = useTranslation('common')

  const handleItemClick = (child: { props: SelectOption }) => () => {
    chooseOption(child.props)
    setSearchTerm(child.props.label)
  }

  const handleDebouncedChange = useMemo(
    () =>
      debounce(
        (value) => {
          onSearchTermChange(value)
        },
        500,
        {
          trailing: true,
        },
      ),
    [onSearchTermChange],
  )

  const handleChange = (e: any) => {
    if (!isSelectOpen) {
      toggleSelect()
    }

    const val = e.target.value
    setSearchTerm(val)
    // onSearchTermChange(val)
    handleDebouncedChange(val)
  }

  useClickOutside(wrapperRef, () => closeSelect())

  return (
    <SelectWrapper ref={wrapperRef} isClickable zIndex={zIndex ?? theme.token.var('zIndex.dropdown')}>
      <div>
        <span>
          <Button
            disabled={disabled}
            isOpen={isSelectOpen}
            onClick={toggleSelect}
            type="button"
            isInvalid={touched && !!error}
            className={className}
          >
            {icon && <Icon src={icon} alt="" />}

            <StyledSearchInput
              isError={!!(touched && error)}
              isSmaller={false}
              name="test"
              value={searchTerm}
              onChange={handleChange}
              onKeyUp={(e) => {
                e.preventDefault()
              }}
              placeholder={placeholder}
              paddingLeft="1rem"
              paddingRight="1rem"
              disabled={disabled}
            />

            {!shouldHideClearButton && chosenOption ? (
              <img
                src="/icons/close.svg"
                alt="Clear"
                onClick={(e) => {
                  e.stopPropagation()
                  toggleSelect()
                  clearOption()
                  setSearchTerm('')
                  onSearchTermChange('')
                  onClear && onClear()
                }}
              />
            ) : (
              <>{isSelectOpen ? <LuChevronUp size={24} /> : <LuChevronDown size={24} />}</>
            )}
          </Button>
        </span>
        {isSelectOpen && (
          <Options>
            {options.map((option, index) => {
              return (
                <Option key={index} onClick={handleItemClick({ props: option })} className="gtm-select-option">
                  <OptionRadio isChecked={chosenOption?.label === option.label}>
                    {chosenOption?.label === option.label ? <img src="/icons/tick.svg" alt="" /> : null}
                  </OptionRadio>
                  <Box>
                    {t(option.label)}
                    {option.subLabel && (
                      <Text color="green.a250" fontSize="xs">
                        {option.subLabel}
                      </Text>
                    )}
                  </Box>
                </Option>
              )
            })}
          </Options>
        )}
      </div>
      <ErrorMessage>{touched && error}</ErrorMessage>
    </SelectWrapper>
  )
}
